import { useDispatch } from 'react-redux';
import { useState } from 'react';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { SOURCE_SELECTIONLIST } from '../../store/constants';
import {
    copyBulkFromListToListByFilter,
    CopyBulkFromSelectionListByFilterRequest
} from './copyBulkFromListToListByFilter';
import {
    copyBulkFromCartToListByLineItemKeys,
    CopyBulkFromShoppingCartByLineItemKeysRequest
} from './copyBulkFromCartToListByLineItemKeys';
import { CopyBulkFromCartByFilterRequest, copyBulkFromCartToListByFilter } from './copyBulkFromCartToListByFilter';
import { ListSourceType } from '../../../models/ListSourceType';
import {
    copyBulkFromListToListByLineItemKeys,
    CopyBulkFromSelectionListByLineItemKeysRequest
} from './copyBulkFromListToListByLineItemKeys';
import { bulkUpdateToList } from './bulkUpdateToList';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';

type GetRequestParams = (
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      }
) & {
    selectionListId: string;
    productSelection: ProductSelection;
    filterRequest: string;
    performChecks: boolean;
};

function executeFilterRequest({
    selectionListId,
    productSelection,
    filterRequest,
    performChecks,
    ...sourceParams
}: GetRequestParams): Promise<BulkAddProductsToSelectionListResponse> {
    const request: CopyBulkFromCartByFilterRequest = {
        filterRequest: deserializeFilter(filterRequest, {
            includeKey: 'include',
            excludeKey: 'exclude'
        }),
        excludedLineItemKeys: isExcludeProductSelection(productSelection) ? productSelection.excluded : []
    };
    const params = { performChecks };

    if (sourceParams.source === SOURCE_SELECTIONLIST) {
        const requestWithSourceList: CopyBulkFromSelectionListByFilterRequest = {
            ...request,
            sourceSelectionListId: sourceParams.sourceSelectionListId
        };
        return copyBulkFromListToListByFilter({ selectionListId, body: requestWithSourceList, params });
    }

    return copyBulkFromCartToListByFilter({ selectionListId, body: request, params });
}

function executeLineItemKeyRequest({
    selectionListId,
    productSelection,
    performChecks,
    ...sourceParams
}: GetRequestParams): Promise<BulkAddProductsToSelectionListResponse> {
    const params = { performChecks };
    const request: CopyBulkFromShoppingCartByLineItemKeysRequest = {
        lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection)
    };
    if (sourceParams.source === SOURCE_SELECTIONLIST) {
        const requestWithSourceList: CopyBulkFromSelectionListByLineItemKeysRequest = {
            ...request,
            sourceSelectionListId: sourceParams.sourceSelectionListId
        };
        return copyBulkFromListToListByLineItemKeys({ selectionListId, body: requestWithSourceList, params });
    }
    return copyBulkFromCartToListByLineItemKeys({ selectionListId, body: request, params });
}

export const buildCopyProductsCallback = (
    { selectionListId, productSelection, filterRequest, performChecks, ...sourceParams }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        const params = { selectionListId, productSelection, filterRequest, performChecks, ...sourceParams };

        if (isIncludeProductSelection(productSelection)) {
            return executeLineItemKeyRequest(params);
        } else if (isExcludeProductSelection(productSelection)) {
            return executeFilterRequest(params);
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props = (
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      }
) & {
    filter: string;
    productSelection: ProductSelection;
};

export const useCopyBulkToSelectionList = ({ filter, productSelection, ...sourceParams }: Props) => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        copyToListBulk: (listId: string, performChecks: boolean) => {
            const apiCallback = buildCopyProductsCallback(
                {
                    selectionListId: listId,
                    productSelection,
                    filterRequest: filter,
                    performChecks,
                    ...sourceParams
                },
                t
            );

            return bulkUpdateToList({
                performChecks,
                dispatch: dispatchRedux,
                setLoading,
                notificationMessage:
                    sourceParams.source === ListSourceType.SELECTION_LIST
                        ? t('notifications.copyProductsToList.message')
                        : t('notifications.copyCartToList.message'),
                apiCallback,
                t,
                onSuccess: () => {}
            });
        }
    };
};
